// ============================================================
// LinuxKernel.jsx
//
// Branded, responsive course content page for the
// "Linux Kernel & Modules" lesson.
//
// Brand palette (consistent across all lesson pages):
//   Background  → #100720  (deep purple-black)
//   Primary     → goldenrod / #DAA520
//   Accent      → #FFD700  (bright gold highlights)
//   Text        → #FFFFFF  (white)
//   Surface     → rgba(255,255,255,0.04) with gold borders
//
// Built with:
//   - Material UI (MUI v5) for layout, typography & icons
//   - Fully responsive (xs → xl breakpoints)
//   - Heavily commented for junior developers
// ============================================================

import React, { useState } from "react";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import MemoryIcon              from "@mui/icons-material/Memory";
import LayersIcon              from "@mui/icons-material/Layers";
import ExtensionIcon           from "@mui/icons-material/Extension";
import SearchIcon              from "@mui/icons-material/Search";
import ArticleIcon             from "@mui/icons-material/Article";
import SystemUpdateIcon        from "@mui/icons-material/SystemUpdate";
import BuildIcon               from "@mui/icons-material/Build";
import TerminalIcon            from "@mui/icons-material/Terminal";
import SettingsIcon            from "@mui/icons-material/Settings";
import SpeedIcon               from "@mui/icons-material/Speed";
import StorageIcon             from "@mui/icons-material/Storage";
import DnsIcon                 from "@mui/icons-material/Dns";
import BugReportIcon           from "@mui/icons-material/BugReport";
import SecurityIcon            from "@mui/icons-material/Security";
import LinkIcon                from "@mui/icons-material/Link";
import ExpandMoreIcon          from "@mui/icons-material/ExpandMore";
import ExpandLessIcon          from "@mui/icons-material/ExpandLess";
import ContentCopyIcon         from "@mui/icons-material/ContentCopy";
import DoneIcon                from "@mui/icons-material/Done";
import CheckCircleOutlineIcon  from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon         from "@mui/icons-material/CheckCircle";
import LightbulbIcon           from "@mui/icons-material/Lightbulb";
import StarIcon                from "@mui/icons-material/Star";
import SchoolIcon              from "@mui/icons-material/School";
import WarningAmberIcon        from "@mui/icons-material/WarningAmber";
import AccountTreeIcon         from "@mui/icons-material/AccountTree";
import TuneIcon                from "@mui/icons-material/Tune";
import DevicesIcon             from "@mui/icons-material/Devices";
import CodeIcon                from "@mui/icons-material/Code";

// ── Brand tokens ──────────────────────────────────────────
// Centralise all colours — change here to retheme everything.
const BRAND = {
    bg:            "#233D4D",
    surface:       "rgba(255,255,255,0.04)",
    border:        "rgba(218,165,32,0.25)",
    borderHover:   "rgba(218,165,32,0.6)",
    gold:          "#DAA520",
    goldBright:    "#FFD700",
    goldGlow:      "rgba(218,165,32,0.15)",
    white:         "#FFFFFF",
    muted:         "rgba(255,255,255,0.55)",
    codeBg:        "rgba(0,0,0,0.45)",
    codeText:      "#FFD700",
    danger:        "rgba(255,80,80,0.12)",
    dangerBorder:  "rgba(255,80,80,0.35)",
    dangerText:    "#ff8080",
    success:       "rgba(39,201,63,0.10)",
    successBorder: "rgba(39,201,63,0.35)",
    successText:   "#27c93f",
    // Distro accent colours — used in the update section
    ubuntu:  "#e95420",   // Ubuntu orange
    fedora:  "#51a2da",   // Fedora blue
    arch:    "#1793d1",   // Arch blue
    debian:  "#d70751",   // Debian red
};

// ── Shared heading style ──────────────────────────────────
const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── MUI TableCell style helper ────────────────────────────
const tcStyle = (extra = {}) => ({
    borderColor: BRAND.border,
    color: BRAND.muted,
    fontSize: 13,
    py: 1.4,
    ...extra,
});

// ============================================================
//  SUB-COMPONENT: SectionTitle
//  Gold icon circle + heading — reused by every section.
//  Props:
//    icon  – MUI icon element
//    label – heading text string
// ============================================================
function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: BRAND.goldGlow,
                    border: `1px solid ${BRAND.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ConceptCard
//  Hoverable card for one kernel concept.
//  Props:
//    icon        – MUI icon element
//    title       – concept name
//    description – plain-English explanation
// ============================================================
function ConceptCard({ icon, title, description }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                height: "100%",
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 3,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 20px ${BRAND.goldGlow}`,
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 22 } })}
                <Typography
                    sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14 }}
                >
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.65 }}>
                {description}
            </Typography>
        </Paper>
    );
}

// ============================================================
//  SUB-COMPONENT: CodeBlock
//  Terminal-style block with copy-to-clipboard.
//  Props:
//    lines – array of { cmd: string, comment?: string }
// ============================================================
function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box
            sx={{
                background: BRAND.codeBg,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                overflow: "hidden",
                mb: 2,
            }}
        >
            {/* ── Terminal title bar ── */}
            <Box
                sx={{
                    px: 2, py: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: `1px solid ${BRAND.border}`,
                    background: "rgba(0,0,0,0.3)",
                }}
            >
                {/* macOS-style decorative dots */}
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>

                {/* Copy button with tick confirmation */}
                <Tooltip title={copied ? "Copied!" : "Copy"} placement="top">
                    <Box
                        onClick={handleCopy}
                        sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: copied ? BRAND.goldBright : BRAND.muted,
                            fontSize: 12,
                            transition: "color 0.2s",
                            "&:hover": { color: BRAND.gold },
                        }}
                    >
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>

            {/* ── Command lines ── */}
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {/* Optional grey comment above each command */}
                        {comment && (
                            <Typography
                                sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.3 }}
                            >
                                # {comment}
                            </Typography>
                        )}
                        <Typography
                            sx={{
                                fontFamily: "monospace",
                                fontSize: { xs: 12, md: 13 },
                                color: BRAND.codeText,
                                wordBreak: "break-all",
                            }}
                        >
                            {cmd}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: WarningBox
//  Red-tinted panel for critical warnings.
// ============================================================
function WarningBox({ children }) {
    return (
        <Box
            sx={{
                display: "flex", alignItems: "flex-start", gap: 1.5,
                p: 2, mb: 2,
                background: BRAND.danger,
                border: `1px solid ${BRAND.dangerBorder}`,
                borderRadius: 2,
            }}
        >
            <WarningAmberIcon sx={{ color: BRAND.dangerText, flexShrink: 0, mt: 0.1 }} />
            <Typography sx={{ color: BRAND.dangerText, fontSize: 13, lineHeight: 1.7 }}>
                {children}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: SuccessBox
//  Green-tinted best-practice tip box.
// ============================================================
function SuccessBox({ children }) {
    return (
        <Box
            sx={{
                display: "flex", alignItems: "flex-start", gap: 1.5,
                p: 2, mb: 2,
                background: BRAND.success,
                border: `1px solid ${BRAND.successBorder}`,
                borderRadius: 2,
            }}
        >
            <CheckCircleIcon sx={{ color: BRAND.successText, flexShrink: 0, mt: 0.1 }} />
            <Typography sx={{ color: BRAND.successText, fontSize: 13, lineHeight: 1.7 }}>
                {children}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ExpandableExample
//  Accordion for optional deeper content.
//  Props:
//    title    – header label
//    icon     – MUI icon element
//    children – body JSX
// ============================================================
function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);

    return (
        <Box
            sx={{
                border: `1px solid ${open ? BRAND.borderHover : BRAND.border}`,
                borderRadius: 2.5,
                overflow: "hidden",
                mb: 1.5,
                transition: "border-color 0.25s",
            }}
        >
            {/* Clickable header */}
            <Box
                onClick={() => setOpen((p) => !p)}
                sx={{
                    px: 2.5, py: 1.8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    background: open ? BRAND.goldGlow : BRAND.surface,
                    transition: "background 0.25s",
                    "&:hover": { background: BRAND.goldGlow },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                    <Typography sx={{ color: BRAND.white, fontWeight: 600, fontSize: 14 }}>
                        {title}
                    </Typography>
                </Box>
                {open
                    ? <ExpandLessIcon sx={{ color: BRAND.gold }} />
                    : <ExpandMoreIcon sx={{ color: BRAND.muted }} />
                }
            </Box>

            {/* MUI Collapse for smooth animation */}
            <Collapse in={open}>
                <Box
                    sx={{
                        px: 2.5, py: 2,
                        background: "rgba(0,0,0,0.25)",
                        borderTop: `1px solid ${BRAND.border}`,
                    }}
                >
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: KernelLayerDiagram
//  Visual diagram showing the kernel's position in the stack:
//  Applications → System Calls → Kernel → Hardware
//  Uses a stacked set of labelled boxes with connecting arrows.
// ============================================================
function KernelLayerDiagram() {
    // Each layer: label, sub-text, colour, and icon
    const layers = [
        {
            label: "User Applications",
            sub: "bash, nginx, python, your code",
            color: "#60a5fa",
            icon: <TerminalIcon sx={{ fontSize: 18 }} />,
        },
        {
            label: "System Call Interface",
            sub: "read(), write(), fork(), exec(), mmap()…",
            color: "#a78bfa",
            icon: <CodeIcon sx={{ fontSize: 18 }} />,
        },
        {
            label: "Linux Kernel",
            sub: "Process mgmt · Memory mgmt · VFS · Networking · Device drivers",
            color: BRAND.gold,
            icon: <MemoryIcon sx={{ fontSize: 18 }} />,
            highlight: true,   // this layer gets extra visual emphasis
        },
        {
            label: "Hardware Abstraction Layer",
            sub: "Architecture-specific code (x86, ARM, RISC-V)",
            color: "#4ade80",
            icon: <DevicesIcon sx={{ fontSize: 18 }} />,
        },
        {
            label: "Physical Hardware",
            sub: "CPU · RAM · Disk · NIC · GPU · Peripherals",
            color: BRAND.goldBright,
            icon: <StorageIcon sx={{ fontSize: 18 }} />,
        },
    ];

    return (
        <Box
            sx={{
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                overflow: "hidden",
                mb: 3,
            }}
        >
            {layers.map(({ label, sub, color, icon, highlight }, i) => (
                <React.Fragment key={label}>
                    {/* Layer row */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            px: { xs: 2, md: 3 },
                            py: 1.8,
                            background: highlight ? `${BRAND.gold}12` : BRAND.surface,
                            borderLeft: `3px solid ${color}`,
                            transition: "background 0.2s",
                            "&:hover": { background: `${color}0a` },
                        }}
                    >
                        {/* Icon in layer colour */}
                        <Box
                            sx={{
                                width: 34,
                                height: 34,
                                borderRadius: 1.5,
                                background: `${color}18`,
                                border: `1px solid ${color}40`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                color,
                            }}
                        >
                            {icon}
                        </Box>

                        {/* Label and sub-text */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: "'Georgia', serif",
                                    fontWeight: highlight ? 800 : 600,
                                    fontSize: { xs: 13, md: 14 },
                                    color: highlight ? BRAND.goldBright : BRAND.white,
                                    lineHeight: 1.2,
                                }}
                            >
                                {label}
                                {highlight && (
                                    <Chip
                                        label="← You are here"
                                        size="small"
                                        sx={{
                                            ml: 1.5,
                                            background: `${BRAND.gold}22`,
                                            border: `1px solid ${BRAND.gold}55`,
                                            color: BRAND.gold,
                                            fontFamily: "monospace",
                                            fontSize: 10,
                                            height: 18,
                                        }}
                                    />
                                )}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: BRAND.muted, mt: 0.3, lineHeight: 1.5 }}>
                                {sub}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Downward arrow between layers (not after the last one) */}
                    {i < layers.length - 1 && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 0.4,
                                background: "rgba(0,0,0,0.2)",
                                borderLeft: `3px solid ${BRAND.border}`,
                            }}
                        >
                            <Typography sx={{ color: BRAND.muted, fontSize: 14, lineHeight: 1 }}>↕</Typography>
                        </Box>
                    )}
                </React.Fragment>
            ))}
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: DistroUpdateCard
//  One card for a distro's kernel update method.
//  Props:
//    name    – distro name
//    pkg     – package manager name
//    color   – accent colour
//    cmd     – update command string
//    notes   – additional context
// ============================================================
function DistroUpdateCard({ name, pkg, color, cmd, notes }) {
    return (
        <Box
            sx={{
                p: 2.5,
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderLeft: `3px solid ${color}`,
                borderRadius: 2,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: color,
                    boxShadow: `0 0 16px ${color}22`,
                },
            }}
        >
            {/* Distro name + pkg manager badge */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                <Typography sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14 }}>
                    {name}
                </Typography>
                <Chip
                    label={pkg}
                    size="small"
                    sx={{
                        background: `${color}18`,
                        border: `1px solid ${color}45`,
                        color,
                        fontFamily: "monospace",
                        fontSize: 10,
                        height: 20,
                    }}
                />
            </Box>

            {/* Command in gold monospace */}
            <Typography
                sx={{
                    fontFamily: "monospace",
                    fontSize: { xs: 12, md: 13 },
                    color: BRAND.codeText,
                    background: "rgba(0,0,0,0.3)",
                    border: `1px solid ${BRAND.border}`,
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1,
                    mb: 1,
                    wordBreak: "break-all",
                }}
            >
                {cmd}
            </Typography>

            {/* Notes */}
            <Typography sx={{ fontSize: 12, color: BRAND.muted, lineHeight: 1.6 }}>
                {notes}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ResourceLink
//  Styled external-link row.
// ============================================================
function ResourceLink({ href, label, sub }) {
    return (
        <Box
            component="a"
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2, mb: 1.2,
                textDecoration: "none",
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                background: BRAND.surface,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 16px ${BRAND.goldGlow}`,
                },
            }}
        >
            <LinkIcon sx={{ color: BRAND.gold, fontSize: 20, flexShrink: 0 }} />
            <Box>
                <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>
                    {label}
                </Typography>
                {sub && (
                    <Typography sx={{ color: BRAND.muted, fontSize: 12, mt: 0.3 }}>{sub}</Typography>
                )}
            </Box>
        </Box>
    );
}

// ============================================================
//  MAIN COMPONENT: LinuxKernel
//  Full "Linux Kernel & Modules" lesson page.
// ============================================================
export default function LinuxKernel() {
    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: BRAND.bg,
                // Subtle diagonal texture — consistent across all lesson pages
                backgroundImage: `repeating-linear-gradient(
          -45deg,
          rgba(218,165,32,0.02) 0px,
          rgba(218,165,32,0.02) 1px,
          transparent 1px,
          transparent 12px
        )`,
                py: { xs: 4, md: 7 },
                px: { xs: 2, sm: 3 },
            }}
        >
            <Container maxWidth="lg">

                {/* ══════════════════════════════════════════════
            HERO HEADER
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>

                    <Chip
                        icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />}
                        label="Advanced · Module 08"
                        sx={{
                            mb: 2.5,
                            background: BRAND.goldGlow,
                            border: `1px solid ${BRAND.border}`,
                            color: BRAND.gold,
                            fontFamily: "monospace",
                            fontSize: 11,
                            letterSpacing: 1,
                            textTransform: "uppercase",
                        }}
                    />

                    <Typography
                        variant="h3"
                        sx={{
                            ...sectionHeading,
                            fontSize: { xs: 26, sm: 34, md: 42 },
                            mb: 2,
                            "& span": {
                                background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldBright})`,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            },
                        }}
                    >
                        Linux <span>Kernel</span> &amp; Modules
                    </Typography>

                    <Typography
                        sx={{
                            color: BRAND.muted,
                            fontSize: { xs: 14, md: 16 },
                            maxWidth: 680,
                            lineHeight: 1.8,
                            mx: { xs: "auto", md: 0 },
                            mb: 3,
                        }}
                    >
                        The kernel is the heart of every Linux system — the bridge between your
                        software and the physical hardware. It manages CPU time, memory allocation,
                        device communication, and security boundaries. Understanding how to inspect,
                        tune, and extend the kernel separates a competent administrator from an expert.
                    </Typography>

                    {/* Quick stats */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        {[
                            { label: "~3 hours",  sub: "read time"   },
                            { label: "Advanced",  sub: "level"        },
                            { label: "6 topics",  sub: "covered"      },
                        ].map(({ label, sub }) => (
                            <Box
                                key={label}
                                sx={{
                                    textAlign: "center",
                                    background: BRAND.surface,
                                    border: `1px solid ${BRAND.border}`,
                                    borderRadius: 2,
                                    px: 2.5, py: 1.2,
                                }}
                            >
                                <Typography sx={{ color: BRAND.goldBright, fontWeight: 800, fontSize: 15 }}>{label}</Typography>
                                <Typography sx={{ color: BRAND.muted, fontSize: 11 }}>{sub}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ══════════════════════════════════════════════
            SECTION 1 – KEY CONCEPTS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LayersIcon />} label="Key Concepts" />

                    <Grid container spacing={2}>
                        {[
                            {
                                icon: <MemoryIcon />,
                                title: "What the Kernel Does",
                                description:
                                    "The kernel mediates all access to hardware. No application talks to the CPU, RAM, or disk directly — it makes system calls, and the kernel handles the rest. This single layer of indirection enables security, multi-tasking, and hardware portability.",
                            },
                            {
                                icon: <AccountTreeIcon />,
                                title: "Monolithic vs Microkernel",
                                description:
                                    "Linux is a monolithic kernel — all core subsystems (filesystem, networking, drivers) run in the same kernel address space for performance. Microkernels (like GNU Mach) run each subsystem in separate user-space processes for isolation.",
                            },
                            {
                                icon: <ExtensionIcon />,
                                title: "Loadable Kernel Modules (LKMs)",
                                description:
                                    "Modules are chunks of kernel code that can be loaded and unloaded at runtime without rebooting. Hardware drivers, filesystems (vfat, ntfs), and network protocols (wireguard) are all modules. This keeps the base kernel small.",
                            },
                            {
                                icon: <SpeedIcon />,
                                title: "Kernel Space vs User Space",
                                description:
                                    "Memory is divided into kernel space (privileged, direct hardware access) and user space (restricted, everything else). Bugs in kernel space crash the system. The system call interface is the controlled gateway between them.",
                            },
                            {
                                icon: <TuneIcon />,
                                title: "sysctl — Runtime Parameters",
                                description:
                                    "sysctl exposes hundreds of kernel parameters as virtual files under /proc/sys/. You can tune networking, memory management, security settings, and more — without recompiling. Changes can be made live or persisted in /etc/sysctl.conf.",
                            },
                            {
                                icon: <SecurityIcon />,
                                title: "Kernel Security",
                                description:
                                    "Modern kernels include SELinux, AppArmor, seccomp, and namespace isolation. These implement mandatory access control, syscall filtering, and process isolation. Keeping the kernel updated is the most important security action for any Linux system.",
                            },
                        ].map((card) => (
                            <Grid item xs={12} sm={6} md={4} key={card.title}>
                                <ConceptCard {...card} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 2 – KERNEL ARCHITECTURE DIAGRAM
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AccountTreeIcon />} label="Kernel Architecture" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        The Linux kernel sits between your running programs and the physical hardware.
                        Every layer can only communicate with its immediate neighbours — user applications
                        cannot access hardware directly; they must ask the kernel via system calls.
                    </Typography>

                    {/* Visual stacked layer diagram */}
                    <KernelLayerDiagram />

                    {/* Kernel subsystems breakdown */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.5 }}>
                        🟡 Kernel Subsystems at a Glance
                    </Typography>
                    <TableContainer
                        component={Box}
                        sx={{ border: `1px solid ${BRAND.border}`, borderRadius: 2, overflowX: "auto", mb: 2 }}
                    >
                        <Table size="small" sx={{ minWidth: 420 }}>
                            <TableHead>
                                <TableRow sx={{ background: BRAND.goldGlow }}>
                                    {["Subsystem", "Responsibility"].map((h) => (
                                        <TableCell key={h} sx={tcStyle({ color: BRAND.gold, fontFamily: "monospace", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", borderColor: BRAND.border })}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[
                                    { sub: "Process Scheduler",   resp: "Decides which process runs on which CPU core and for how long (CFS — Completely Fair Scheduler)" },
                                    { sub: "Memory Manager",       resp: "Manages physical RAM, virtual memory, page tables, swap, and the buffer/page cache" },
                                    { sub: "Virtual File System",  resp: "Unified interface over all filesystem types (ext4, xfs, btrfs, ntfs, proc, sysfs)" },
                                    { sub: "Network Stack",        resp: "Implements TCP/IP, UDP, routing, netfilter (iptables), and socket API" },
                                    { sub: "Device Drivers",       resp: "Translate generic kernel requests into hardware-specific commands (most run as modules)" },
                                    { sub: "IPC (Inter-Process)",  resp: "Pipes, signals, message queues, semaphores, and shared memory between processes" },
                                    { sub: "Security Modules",     resp: "SELinux, AppArmor, seccomp, capabilities — enforce mandatory access control policies" },
                                ].map(({ sub, resp }) => (
                                    <TableRow key={sub} sx={{ "&:hover": { background: "rgba(255,255,255,0.02)" }, transition: "background 0.2s" }}>
                                        <TableCell sx={tcStyle({ color: BRAND.goldBright, fontFamily: "monospace", fontWeight: 600, borderColor: BRAND.border, minWidth: 160 })}>{sub}</TableCell>
                                        <TableCell sx={tcStyle({ borderColor: BRAND.border })}>{resp}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 3 – CHECKING KERNEL VERSION
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SearchIcon />} label="Checking Kernel Version & Info" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        Before tuning or updating the kernel, always identify exactly what is running.
                        The version string tells you the kernel release, build date, architecture, and compiler.
                    </Typography>

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Version & System Info
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ uname -r",             comment: "Print kernel release version only (e.g. 6.5.0-35-generic)" },
                            { cmd: "$ uname -a",             comment: "Print all: kernel name, hostname, release, version, arch" },
                            { cmd: "$ uname -m",             comment: "Print machine hardware architecture (x86_64, aarch64, etc.)" },
                            { cmd: "$ uname -v",             comment: "Print kernel version string including build date" },
                            { cmd: "$ cat /proc/version",    comment: "Read version info directly from the kernel virtual filesystem" },
                            { cmd: "$ hostnamectl",          comment: "Show system info including kernel via systemd (modern distros)" },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Installed Kernels
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ dpkg --list | grep linux-image",        comment: "List all installed kernel packages on Ubuntu/Debian" },
                            { cmd: "$ rpm -qa | grep kernel",                 comment: "List installed kernel packages on Fedora/CentOS/RHEL" },
                            { cmd: "$ ls /boot/vmlinuz*",                     comment: "List kernel image files in /boot directly" },
                            { cmd: "$ ls /boot/",                             comment: "See all boot files: kernel, initrd, grub config" },
                            { cmd: "$ sudo update-grub",                      comment: "Regenerate GRUB config after installing a new kernel (Ubuntu)" },
                            { cmd: "$ cat /boot/grub/grub.cfg | grep menuentry", comment: "View all GRUB boot menu entries including older kernels" },
                        ]}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 4 – KERNEL MODULES
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<ExtensionIcon />} label="Kernel Modules" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        Kernel modules (<code style={{ color: BRAND.goldBright }}>.ko</code> files) extend
                        the kernel at runtime. When you plug in a USB drive, the kernel automatically loads
                        the appropriate driver module. You can also load and unload modules manually —
                        useful for debugging, hardware support, and custom drivers.
                    </Typography>

                    {/* Core module commands */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Listing & Inspecting Modules
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ lsmod",                           comment: "List all currently loaded kernel modules, their size and dependencies" },
                            { cmd: "$ lsmod | grep usb",                comment: "Filter for USB-related modules" },
                            { cmd: "$ modinfo vfat",                    comment: "Show metadata about a module: author, license, parameters, filename" },
                            { cmd: "$ modinfo nvidia",                  comment: "Inspect the NVIDIA driver module (if installed)" },
                            { cmd: "$ find /lib/modules -name '*.ko'",  comment: "Find all available module files for the running kernel" },
                            { cmd: "$ find /lib/modules/$(uname -r) -name '*.ko' | wc -l", comment: "Count total available modules for this kernel" },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Loading & Unloading Modules
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo modprobe vfat",              comment: "Load module and any dependencies automatically (preferred method)" },
                            { cmd: "$ sudo modprobe -v vfat",           comment: "Load with verbose output — shows exactly what is being loaded" },
                            { cmd: "$ sudo modprobe -r vfat",           comment: "Remove module and any unused dependencies" },
                            { cmd: "$ sudo insmod /path/to/module.ko",  comment: "Insert a specific .ko file directly (no dependency resolution)" },
                            { cmd: "$ sudo rmmod vfat",                 comment: "Remove a loaded module by name (fails if module is in use)" },
                            { cmd: "$ sudo modprobe --dry-run vfat",    comment: "Simulate loading — shows what would happen without doing it" },
                        ]}
                    />

                    {/* Warning about rmmod */}
                    <WarningBox>
                        Never <code>rmmod</code> a module that is currently in use. Removing an active
                        filesystem driver or network driver mid-operation can corrupt data or crash the
                        system. Always check with <code>lsmod</code> and ensure nothing depends on it
                        (the "Used by" column must be 0).
                    </WarningBox>

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 2 }}>
                        🟡 Persistent Module Configuration
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ cat /etc/modules",                           comment: "Modules listed here are loaded automatically at boot" },
                            { cmd: "$ echo 'vfat' | sudo tee -a /etc/modules",    comment: "Add vfat to the list of boot-time auto-loaded modules" },
                            { cmd: "$ ls /etc/modules-load.d/",                   comment: "Drop-in directory for module loading configs (systemd systems)" },
                            { cmd: "$ ls /etc/modprobe.d/",                       comment: "Module options and blacklist configs live here" },
                            { cmd: "$ cat /etc/modprobe.d/blacklist.conf",        comment: "View blacklisted modules — these will never auto-load" },
                            { cmd: "$ echo 'blacklist nouveau' | sudo tee /etc/modprobe.d/blacklist-nouveau.conf", comment: "Blacklist the Nouveau GPU driver (before installing NVIDIA)" },
                            { cmd: "$ sudo depmod -a",                            comment: "Rebuild the module dependency map after adding new modules" },
                        ]}
                    />

                    <ExpandableExample title="Writing a minimal 'Hello World' kernel module" icon={<CodeIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            Understanding kernel modules starts with writing one. This minimal example
                            prints to the kernel log on load and unload. You will need the kernel headers
                            installed: <code style={{ color: BRAND.goldBright }}>sudo apt install linux-headers-$(uname -r)</code>.
                        </Typography>
                        <Typography sx={{ color: BRAND.white, fontSize: 12, fontWeight: 700, mb: 0.8 }}>
                            hello.c
                        </Typography>
                        <Box
                            sx={{
                                background: BRAND.codeBg,
                                border: `1px solid ${BRAND.border}`,
                                borderRadius: 2,
                                p: 2,
                                mb: 2,
                                overflowX: "auto",
                            }}
                        >
                            {[
                                { line: "#include <linux/module.h>",                               },
                                { line: "#include <linux/kernel.h>",                               },
                                { line: "#include <linux/init.h>",                                 },
                                { line: "",                                                         },
                                { line: "MODULE_LICENSE(\"GPL\");",                                },
                                { line: "MODULE_AUTHOR(\"Your Name\");",                           },
                                { line: "MODULE_DESCRIPTION(\"A minimal hello-world module\");",   },
                                { line: "",                                                         },
                                { line: "static int __init hello_init(void) {",                   },
                                { line: '    printk(KERN_INFO "Hello, Kernel!\\n");',              },
                                { line: "    return 0;",                                           },
                                { line: "}",                                                       },
                                { line: "",                                                         },
                                { line: "static void __exit hello_exit(void) {",                  },
                                { line: '    printk(KERN_INFO "Goodbye, Kernel!\\n");',            },
                                { line: "}",                                                       },
                                { line: "",                                                         },
                                { line: "module_init(hello_init);",                               },
                                { line: "module_exit(hello_exit);",                               },
                            ].map((entry, i) => (
                                <Typography key={i} sx={{ fontFamily: "monospace", fontSize: 12, color: entry.line.startsWith("//") || entry.line.startsWith("/*") ? "rgba(255,255,255,0.35)" : BRAND.codeText, lineHeight: 1.85 }}>
                                    {entry.line || "\u00A0"}
                                </Typography>
                            ))}
                        </Box>

                        <Typography sx={{ color: BRAND.white, fontSize: 12, fontWeight: 700, mb: 0.8 }}>
                            Makefile
                        </Typography>
                        <Box
                            sx={{
                                background: BRAND.codeBg,
                                border: `1px solid ${BRAND.border}`,
                                borderRadius: 2,
                                p: 2,
                                mb: 2,
                                overflowX: "auto",
                            }}
                        >
                            {[
                                "obj-m += hello.o",
                                "",
                                "all:",
                                "\tmake -C /lib/modules/$(shell uname -r)/build M=$(PWD) modules",
                                "clean:",
                                "\tmake -C /lib/modules/$(shell uname -r)/build M=$(PWD) clean",
                            ].map((line, i) => (
                                <Typography key={i} sx={{ fontFamily: "monospace", fontSize: 12, color: BRAND.codeText, lineHeight: 1.85 }}>
                                    {line || "\u00A0"}
                                </Typography>
                            ))}
                        </Box>

                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo apt install linux-headers-$(uname -r) build-essential", comment: "Install build dependencies" },
                                { cmd: "$ make",                    comment: "Compile the module — produces hello.ko" },
                                { cmd: "$ sudo insmod hello.ko",    comment: "Load the module" },
                                { cmd: "$ dmesg | tail -5",         comment: "See 'Hello, Kernel!' in the kernel log" },
                                { cmd: "$ sudo rmmod hello",        comment: "Unload the module" },
                                { cmd: "$ dmesg | tail -3",         comment: "See 'Goodbye, Kernel!' in the log" },
                            ]}
                        />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 5 – KERNEL LOGS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<ArticleIcon />} label="Kernel Logs & Diagnostics" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        The kernel maintains a circular ring buffer of log messages called the{" "}
                        <strong style={{ color: BRAND.white }}>kernel ring buffer</strong>. Every hardware
                        detection event, driver error, OOM kill, filesystem error, and security violation is
                        written here first. Reading these logs is how you diagnose hardware problems,
                        driver failures, and kernel panics.
                    </Typography>

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Reading the Kernel Ring Buffer
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ dmesg",                                      comment: "Print the entire kernel ring buffer" },
                            { cmd: "$ dmesg | less",                               comment: "Page through the output (q to quit)" },
                            { cmd: "$ dmesg -H",                                   comment: "Human-readable timestamps and colours (recent kernels)" },
                            { cmd: "$ dmesg -T",                                   comment: "Convert timestamps to human-readable dates" },
                            { cmd: "$ dmesg | tail -20",                           comment: "Show the 20 most recent kernel messages" },
                            { cmd: "$ dmesg -w",                                   comment: "Follow new kernel messages in real time (like tail -f)" },
                            { cmd: "$ dmesg --level=err,warn",                     comment: "Filter: show only errors and warnings" },
                            { cmd: "$ dmesg | grep -i 'error\\|fail\\|warn'",      comment: "Search for problems in the ring buffer" },
                            { cmd: "$ dmesg | grep -i usb",                        comment: "See USB device detection and driver messages" },
                            { cmd: "$ sudo dmesg -C",                              comment: "Clear the ring buffer (useful before reproducing a bug)" },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 systemd Journal (Persistent Kernel Logs)
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ journalctl -k",                              comment: "Show all kernel messages from the systemd journal" },
                            { cmd: "$ journalctl -k -b",                           comment: "Kernel messages from the current boot only" },
                            { cmd: "$ journalctl -k -b -1",                        comment: "Kernel messages from the previous boot (-1 = one boot ago)" },
                            { cmd: "$ journalctl -k -p err",                       comment: "Kernel errors only (priorities: emerg, alert, crit, err, warning, notice, info, debug)" },
                            { cmd: "$ journalctl -k -f",                           comment: "Follow new kernel journal entries in real time" },
                            { cmd: "$ journalctl -k --since '1 hour ago'",         comment: "Kernel messages from the last hour" },
                            { cmd: "$ journalctl -k --since '2025-01-15 02:00'",   comment: "Kernel messages since a specific date and time" },
                        ]}
                    />

                    <ExpandableExample title="Diagnosing hardware issues with dmesg" icon={<BugReportIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            dmesg is your first stop for hardware troubleshooting. Here are the most
                            useful patterns to search for when diagnosing specific problems.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ dmesg | grep -i 'oom'",                   comment: "Find Out-of-Memory killer events (process killed due to low RAM)" },
                                { cmd: "$ dmesg | grep -i 'mce'",                   comment: "Machine Check Exception — indicates CPU or memory hardware error" },
                                { cmd: "$ dmesg | grep -i 'ata\\|sata\\|scsi'",     comment: "Disk-related messages — look for I/O errors" },
                                { cmd: "$ dmesg | grep -i 'wlan\\|wifi\\|80211'",   comment: "WiFi driver and firmware messages" },
                                { cmd: "$ dmesg | grep -i 'segfault'",              comment: "Application segmentation faults recorded in kernel log" },
                                { cmd: "$ dmesg | grep -i 'firmware'",              comment: "Missing or failed firmware load messages" },
                                { cmd: "$ dmesg | grep -i 'drm\\|gpu\\|nvidia\\|amdgpu'", comment: "GPU driver messages and errors" },
                            ]}
                        />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 6 – SYSCTL TUNING
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TuneIcon />} label="sysctl — Runtime Kernel Tuning" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        <code style={{ color: BRAND.goldBright }}>sysctl</code> reads and writes kernel
                        parameters exposed as files under <code style={{ color: BRAND.goldBright }}>/proc/sys/</code>.
                        Changes take effect immediately — no reboot, no kernel recompile. Persist them
                        in <code style={{ color: BRAND.goldBright }}>/etc/sysctl.conf</code> or a file
                        in <code style={{ color: BRAND.goldBright }}>/etc/sysctl.d/</code>.
                    </Typography>

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Reading & Writing Parameters
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sysctl -a",                                         comment: "Print all current kernel parameters (thousands of them)" },
                            { cmd: "$ sysctl -a | grep net.ipv4",                         comment: "Filter for IPv4 networking parameters" },
                            { cmd: "$ sysctl kernel.hostname",                             comment: "Read one specific parameter" },
                            { cmd: "$ sudo sysctl -w net.ipv4.ip_forward=1",              comment: "Enable IP forwarding (needed for routing/NAT) — immediate effect" },
                            { cmd: "$ sudo sysctl -w vm.swappiness=10",                   comment: "Reduce swap usage aggressiveness (0-100, default 60)" },
                            { cmd: "$ cat /proc/sys/net/ipv4/ip_forward",                 comment: "Read a parameter directly from the virtual filesystem" },
                            { cmd: "$ echo 1 | sudo tee /proc/sys/net/ipv4/ip_forward",   comment: "Write a parameter directly (same as sysctl -w)" },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.5, mt: 3 }}>
                        🟡 Common Tuning Parameters
                    </Typography>
                    <TableContainer
                        component={Box}
                        sx={{ border: `1px solid ${BRAND.border}`, borderRadius: 2, overflowX: "auto", mb: 3 }}
                    >
                        <Table size="small" sx={{ minWidth: 460 }}>
                            <TableHead>
                                <TableRow sx={{ background: BRAND.goldGlow }}>
                                    {["Parameter", "Default", "Tuned", "Purpose"].map((h) => (
                                        <TableCell key={h} sx={tcStyle({ color: BRAND.gold, fontFamily: "monospace", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", borderColor: BRAND.border })}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[
                                    { param: "vm.swappiness",                  def: "60",     tuned: "10",      purpose: "How aggressively to use swap. Lower = prefer RAM." },
                                    { param: "net.ipv4.ip_forward",            def: "0",      tuned: "1",       purpose: "Enable IP routing/forwarding between interfaces." },
                                    { param: "net.core.somaxconn",             def: "4096",   tuned: "65535",   purpose: "Max listen backlog for incoming connections." },
                                    { param: "net.ipv4.tcp_syncookies",        def: "1",      tuned: "1",       purpose: "Protect against SYN flood DoS attacks." },
                                    { param: "fs.file-max",                    def: "varies", tuned: "2097152", purpose: "Maximum open file descriptors system-wide." },
                                    { param: "kernel.pid_max",                 def: "32768",  tuned: "4194304", purpose: "Maximum process ID — increase for busy servers." },
                                    { param: "vm.dirty_ratio",                 def: "20",     tuned: "10",      purpose: "% of RAM allowed dirty before synchronous write." },
                                    { param: "net.ipv4.tcp_tw_reuse",          def: "0",      tuned: "1",       purpose: "Reuse TIME_WAIT sockets for new connections." },
                                ].map(({ param, def, tuned, purpose }) => (
                                    <TableRow key={param} sx={{ "&:hover": { background: "rgba(255,255,255,0.02)" }, transition: "background 0.2s" }}>
                                        <TableCell sx={tcStyle({ color: BRAND.goldBright, fontFamily: "monospace", fontSize: 12, borderColor: BRAND.border })}>{param}</TableCell>
                                        <TableCell sx={tcStyle({ fontFamily: "monospace", borderColor: BRAND.border })}>{def}</TableCell>
                                        <TableCell sx={tcStyle({ fontFamily: "monospace", color: BRAND.successText, borderColor: BRAND.border })}>{tuned}</TableCell>
                                        <TableCell sx={tcStyle({ borderColor: BRAND.border })}>{purpose}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Persisting sysctl Changes
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo nano /etc/sysctl.conf",                       comment: "Edit the main sysctl config file" },
                            { cmd: "$ echo 'vm.swappiness=10' | sudo tee /etc/sysctl.d/99-custom.conf", comment: "Add a parameter to a drop-in config (preferred on modern distros)" },
                            { cmd: "$ sudo sysctl --system",                             comment: "Apply all sysctl config files without rebooting" },
                            { cmd: "$ sudo sysctl -p",                                   comment: "Reload /etc/sysctl.conf specifically" },
                            { cmd: "$ sudo sysctl -p /etc/sysctl.d/99-custom.conf",     comment: "Reload a specific drop-in file" },
                        ]}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 7 – UPDATING THE KERNEL
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SystemUpdateIcon />} label="Updating the Kernel" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        Keeping the kernel updated is the single most important security action for a
                        Linux system. Kernel updates patch vulnerabilities (Spectre, Meltdown, privilege
                        escalation), fix driver bugs, and improve hardware support. The method depends
                        on your distribution.
                    </Typography>

                    <WarningBox>
                        Always keep the <strong>previous working kernel</strong> available in your bootloader.
                        If an updated kernel fails to boot, you can select the old one at the GRUB menu.
                        Never uninstall a kernel until you have confirmed the new one boots and runs correctly.
                    </WarningBox>

                    {/* Per-distro update cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {[
                            {
                                name: "Ubuntu / Linux Mint",
                                pkg: "apt",
                                color: BRAND.ubuntu,
                                cmd: "$ sudo apt update && sudo apt full-upgrade",
                                notes: "Kernel updates arrive via normal package upgrades. Use 'linux-generic' for the standard kernel or 'linux-hwe-22.04' for HWE (Hardware Enablement) kernels with newer drivers.",
                            },
                            {
                                name: "Debian",
                                pkg: "apt",
                                color: BRAND.debian,
                                cmd: "$ sudo apt update && sudo apt upgrade linux-image-amd64",
                                notes: "Debian stable ships conservative kernel versions. The backports repository offers newer kernels: add 'bookworm-backports' to sources.list.",
                            },
                            {
                                name: "Fedora",
                                pkg: "dnf",
                                color: BRAND.fedora,
                                cmd: "$ sudo dnf update kernel",
                                notes: "Fedora ships close to the latest upstream kernel. DNF automatically keeps the last 3 kernel versions in GRUB.",
                            },
                            {
                                name: "Arch Linux",
                                pkg: "pacman",
                                color: BRAND.arch,
                                cmd: "$ sudo pacman -Syu",
                                notes: "Arch uses rolling releases — the kernel updates with every full system upgrade. Read the Arch news page before upgrading in case of manual intervention steps.",
                            },
                        ].map((card) => (
                            <Grid item xs={12} sm={6} key={card.name}>
                                <DistroUpdateCard {...card} />
                            </Grid>
                        ))}
                    </Grid>

                    {/* Post-update steps */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 After a Kernel Update
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo update-grub",                                  comment: "Regenerate GRUB menu to include the new kernel (Ubuntu/Debian)" },
                            { cmd: "$ sudo grub2-mkconfig -o /boot/grub2/grub.cfg",       comment: "Regenerate GRUB on Fedora/RHEL" },
                            { cmd: "$ sudo reboot",                                        comment: "Reboot to boot into the new kernel" },
                            { cmd: "$ uname -r",                                           comment: "After reboot: confirm you are running the new kernel" },
                            { cmd: "$ dmesg | grep -i error",                              comment: "Check for any errors introduced by the new kernel" },
                            { cmd: "$ dpkg --list | grep linux-image",                     comment: "List all installed kernels — remove old ones once stable" },
                            { cmd: "$ sudo apt autoremove",                                 comment: "Remove old kernel packages automatically (Ubuntu/Debian)" },
                        ]}
                    />

                    <ExpandableExample title="Compiling a custom kernel from source" icon={<BuildIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            Compiling your own kernel lets you enable or disable specific features, apply
                            custom patches, and optimise for your exact hardware. This is advanced — only
                            needed for embedded development, specialised hardware, or kernel contribution.
                            Takes 30–90 minutes on a typical machine.
                        </Typography>
                        <WarningBox>
                            Always test compiled kernels in a VM or on non-production hardware first.
                            A kernel that fails to boot can leave a system unreachable if you don't
                            have physical or console access to select an older kernel at GRUB.
                        </WarningBox>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo apt install build-essential libncurses-dev libssl-dev libelf-dev bison flex",  comment: "Install build dependencies" },
                                { cmd: "$ wget https://cdn.kernel.org/pub/linux/kernel/v6.x/linux-6.5.tar.xz",              comment: "Download kernel source (check kernel.org for latest version)" },
                                { cmd: "$ tar -xf linux-6.5.tar.xz && cd linux-6.5",                                        comment: "Extract and enter source directory" },
                                { cmd: "$ cp /boot/config-$(uname -r) .config",                                             comment: "Copy current kernel config as a starting point" },
                                { cmd: "$ make menuconfig",                                                                  comment: "Interactive config menu — enable/disable features" },
                                { cmd: "$ make -j$(nproc)",                                                                  comment: "Compile using all available CPU cores (nproc prints the count)" },
                                { cmd: "$ sudo make modules_install",                                                        comment: "Install the compiled modules to /lib/modules/" },
                                { cmd: "$ sudo make install",                                                                comment: "Install the kernel image and update initramfs + GRUB" },
                                { cmd: "$ sudo reboot",                                                                      comment: "Reboot and select the new kernel from GRUB" },
                            ]}
                        />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 8 – WHAT YOU HAVE LEARNED
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="What You've Learned" />

                    <Grid container spacing={1.5}>
                        {[
                            "What the Linux kernel does and how it sits between software and hardware",
                            "The difference between monolithic and microkernel designs",
                            "The key kernel subsystems: scheduler, memory manager, VFS, network stack",
                            "How to check the running kernel version and list all installed kernels",
                            "What kernel modules are and how loadable modules extend the kernel",
                            "How to list, inspect, load, unload, and blacklist kernel modules",
                            "How to write and build a minimal kernel module from C source",
                            "How to read the kernel ring buffer with dmesg and filter for errors",
                            "How to read persistent kernel logs via the systemd journal",
                            "How to read and write sysctl kernel parameters at runtime",
                            "How to persist sysctl changes in /etc/sysctl.d/ across reboots",
                            "How to update the kernel on Ubuntu, Debian, Fedora, and Arch",
                            "How to compile a custom kernel from source at a high level",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1, flexShrink: 0 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.6 }}>
                                        {item}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 9 – RESOURCES
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 4 }}>
                    <SectionTitle icon={<LinkIcon />} label="Further Resources" />

                    <ResourceLink
                        href="https://kernel.org"
                        label="kernel.org — The Official Linux Kernel Archive"
                        sub="Download stable, mainline, and longterm kernel sources. The authoritative source."
                    />
                    <ResourceLink
                        href="https://wiki.archlinux.org/title/Kernel"
                        label="Arch Wiki — Kernel"
                        sub="Comprehensive guide covering installation, compilation, and configuration on any distro."
                    />
                    <ResourceLink
                        href="https://linuxhint.com/linux_kernel_modules/"
                        label="Linux Kernel Modules — LinuxHint"
                        sub="Practical guide to lsmod, modprobe, insmod, rmmod, and module configuration."
                    />
                    <ResourceLink
                        href="https://www.kernel.org/doc/html/latest/"
                        label="Linux Kernel Documentation"
                        sub="Official kernel documentation including subsystem internals, API references, and driver guides."
                    />
                    <ResourceLink
                        href="https://www.brendangregg.com/linuxperf.html"
                        label="Linux Performance — Brendan Gregg"
                        sub="Expert-level reference for kernel performance tools including dmesg, perf, ftrace, and BPF."
                    />
                    <ResourceLink
                        href="https://sysprog21.github.io/lkmpg/"
                        label="The Linux Kernel Module Programming Guide"
                        sub="Free online book covering everything from hello-world modules to character devices and interrupts."
                    />
                </Box>

                {/* ── Footer pro tip ── */}
                <Box
                    sx={{
                        mt: 2, p: 2.5,
                        border: `1px solid ${BRAND.border}`,
                        borderRadius: 2,
                        background: BRAND.goldGlow,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                    }}
                >
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0, mt: 0.1 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.7 }}>
                        <strong style={{ color: BRAND.white }}>Pro tip for juniors:</strong> Don't try to
                        understand the kernel all at once. Start with{" "}
                        <code style={{ color: BRAND.goldBright }}>uname -r</code> and{" "}
                        <code style={{ color: BRAND.goldBright }}>lsmod</code>, then graduate to reading{" "}
                        <code style={{ color: BRAND.goldBright }}>dmesg</code> when something goes wrong.
                        The kernel communicates everything through its logs — learning to read them is the
                        single most valuable debugging skill you can develop as a Linux administrator.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
